<template>
  <div>
    <el-dialog
      :visible.sync="dialogVisible"
      :before-close="handleClose"
      width="90%"
      top="10px"
      custom-class="mask-editor-dialog"
    >
      <div class="editor-container" v-loading="cropMaskloading">
        <div class="tools-panel">
          <div class="tool-group">
            <div class="tool-buttons">
              <button
                class="btn btn-undo"
                @click="undo"
                :disabled="undoStack.length === 0"
              >
                <span>↩️ 撤销 ({{ undoStack.length }})</span>
              </button>
              <button
                class="btn btn-redo"
                @click="redo"
                :disabled="redoStack.length === 0"
              >
                <span>↪️ 重做 ({{ redoStack.length }})</span>
              </button>
              <button
                class="btn btn-erase"
                @click="toggleEraseMode"
                :style="{ background: isErasing ? '#e74c3c' : '#3498db' }"
              >
                <span v-if="isErasing">🎨 画笔模式</span>
                <span v-else>🧹 橡皮擦模式</span>
              </button>
            </div>
            <div class="key-hint">
              <div class="key">Ctrl+Z</div>
              撤销 &nbsp;&nbsp;
              <div class="key">Ctrl+Y</div>
              重做&nbsp;&nbsp;
              <div class="key">鼠标滚轮</div>
              缩放 &nbsp;&nbsp;
              <div class="key">空格+鼠标左键</div>
              拖拽
            </div>
          </div>

          <div class="tool-group">
            <h2 class="tool-title">
              <span><i class="el-icon-edit"></i> 设置</span>
            </h2>
            <div class="slider-container">
              <span>画笔大小:</span>
              <input
                type="range"
                min="1"
                max="200"
                v-model.number="brushSize"
              />
              <div class="slider-value">{{ brushSize }}px</div>
            </div>

            <div class="slider-container">
              <span>缩放比例:</span>
              <input
                type="range"
                min="10"
                max="200"
                v-model.number="scalePercent"
              />
              <div class="slider-value">{{ scalePercent }}%</div>
            </div>
          </div>

          <div class="tool-group">
            <div class="tool-buttons">
              <button class="btn" @click="zoomIn">
                <span><i class="el-icon-zoom-in"></i> 放大</span>
              </button>
              <button class="btn" @click="zoomOut">
                <span><i class="el-icon-zoom-out"></i> 缩小</span>
              </button>
              <button class="btn" @click="resetZoom">
                <span><i class="el-icon-refresh-left"></i> 重置</span>
              </button>
              <button class="btn" @click="centerCanvas">
                <span><i class="el-icon-aim"></i> 居中</span>
              </button>
            </div>
          </div>

          <button class="btn btn-generate" @click="generateMask">
            <span>生成遮罩</span>
          </button>
          <button class="btn btn-generate" @click="sureMask">
            <span>确认遮罩</span>
          </button>
        </div>

        <div class="canvas-wrapper">
          <div
            class="canvas-container"
            ref="container"
            @mousemove="updateCursorPosition"
            :class="{ panning: spacePressed, dragging: isDragging }"
          >
            <canvas ref="bgCanvas" class="bg-canvas"></canvas>
            <canvas
              ref="fgCanvas"
              class="fg-canvas"
              @mousedown="handleCanvasMouseDown"
              @mousemove="handleCanvasMouseMove"
              @mouseup="handleCanvasMouseUp"
              @mouseleave="handleCanvasMouseLeave"
              @wheel.prevent="handleWheel"
            ></canvas>
            <div v-if="cursorVisible" class="cursor" :style="cursorStyle"></div>

            <div class="zoom-controls">
              <div class="zoom-display">{{ scalePercent }}%</div>
              <button class="btn" @click="zoomIn">
                <span><i class="el-icon-zoom-in"></i></span>
              </button>
              <button class="btn" @click="zoomOut">
                <span><i class="el-icon-zoom-out"></i></span>
              </button>
              <button class="btn" @click="resetZoom">
                <span><i class="el-icon-refresh-left"></i></span>
              </button>
            </div>

            <div class="status-bar">
              画布坐标: {{ mouseX.toFixed(0) }}, {{ mouseY.toFixed(0) }} | 模式: {{ isErasing ? "橡皮擦" : "画笔" }}
            </div>

            <div class="drag-hint" v-if="spacePressed">
              <span v-if="!isDragging">按住左键拖拽画布</span>
              <span v-else>拖拽中...</span>
            </div>
          </div>
        </div>
      </div>

      <div class="mask-preview">
        <div class="preview-container">
          <div class="preview-item">
            <h3>原始图片</h3>
            <div class="preview-image">
              <img :src="imageUrl" alt="原始图片" />
            </div>
          </div>
          <div class="preview-item">
            <h3>涂抹抠图结果</h3>
            <div class="preview-image">
              <el-image
                :src="binaryPreview"
                v-if="binaryPreview"
                style="width: 100%;height: 200px;"
                fit="contain"
                :preview-src-list="[binaryPreview]"
              ></el-image>
              <div v-else>生成后将显示预览</div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  uploadImage,
  segment,
} from "@/api/template";
export default {
  name: "CutOut",
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    imageUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      cropMaskloading:false,
      dialogVisible: this.visible,
      bgCanvas: null,
      bgCtx: null,
      fgCanvas: null,
      fgCtx: null,
      scale: 1,
      scalePercent: 100,
      isDrawing: false,
      brushSize: 15,
      isErasing: false,
      undoStack: [],
      redoStack: [],
      cursorVisible: false,
      binaryPreview: null,
      offset: { x: 0, y: 0 },
      mouseX: 0,
      mouseY: 0,
      cursorX: 0,
      cursorY: 0,
      spacePressed: false,
      isDragging: false,
      ishandleWheel: false,
      lastDragPos: { x: 0, y: 0 },
      originalImage: null,
      showAlert: false,
      alertMessage: "",
      image:null,
    };
  },
  computed: {
    cursorStyle() {
      const size = this.brushSize * this.scale;
      return {
        width: `${size}px`,
        height: `${size}px`,
        left: `${this.cursorX}px`,
        top: `${this.cursorY}px`,
        borderColor: this.isErasing ? "#e74c3c" : "#0095FF",
      };
    },
  },
  watch: {
    visible(val) {
      this.dialogVisible = val;
      if (val) {
        this.$nextTick(() => {
          this.initCanvas();
        });
      }
    },
    dialogVisible(val) {
      this.$emit("update:visible", val);
    },
    brushSize() {
      if (this.fgCtx) {
        this.fgCtx.lineWidth = this.brushSize;
      }
    },
    scalePercent(val) {
      this.scale = val / 100;
      this.centerCanvas();
    },
  },
  methods: {
    loadImage(url) {
      return new Promise((resolve) => {
        this.image = new Image();
        this.image.crossOrigin = "anonymous";
        this.image.onload = () => {
          resolve();
        };
        this.image.src = url;
      });
    },
    async initCanvas() {
      // 初始化背景画布（原始图像）
      this.bgCanvas = this.$refs.bgCanvas;
      this.bgCtx = this.bgCanvas.getContext("2d");

      // 初始化前景画布（涂抹层）
      this.fgCanvas = this.$refs.fgCanvas;
      this.fgCtx = this.fgCanvas.getContext("2d");
      this.binaryPreview = null;
      this.undoStack=[],
      this.redoStack=[],
      await this.loadImage(this.imageUrl);
      const img = this.image;
      this.originalImage = this.image;

      // 设置画布尺寸
      this.bgCanvas.width = img.width;
      this.bgCanvas.height = img.height;
      this.fgCanvas.width = img.width;
      this.fgCanvas.height = img.height;
      this.scalePercent = Number(((600/img.height) * 100).toFixed(1));

      // 绘制背景图像
      this.bgCtx.drawImage(img, 0, 0);

      // 初始化前景画布
      this.clearForeground();

      // 设置初始画笔样式
      this.fgCtx.strokeStyle = "rgb(0, 149, 255)";
      this.fgCtx.lineWidth = this.brushSize;
      this.fgCtx.lineCap = "round";
      this.fgCtx.lineJoin = "round";

      // 保存初始状态
      this.saveState();

      // 设置初始位置
      this.centerCanvas();

      // 添加事件监听
      window.addEventListener("resize", this.handleResize);
      window.addEventListener("keydown", this.handleKeyDown);
      window.addEventListener("keyup", this.handleKeyUp);
    },

    clearForeground() {
      this.fgCtx.clearRect(0, 0, this.fgCanvas.width, this.fgCanvas.height);
      this.fgCtx.fillStyle = "rgba(0,0,0,0)";
      this.fgCtx.fillRect(0, 0, this.fgCanvas.width, this.fgCanvas.height);
    },

    centerCanvas() {
      if(!this.ishandleWheel){
        const container = this.$refs.container;
        this.offset.x =
          (container.clientWidth - this.fgCanvas.width * this.scale) / 2;
        this.offset.y =
          (container.clientHeight - this.fgCanvas.height * this.scale) / 2;
      }else{
        this.ishandleWheel = false;
      }
      this.applyTransform();
    },

   

    handleCanvasMouseDown(e) {
      if (this.spacePressed) {
        // 空格键拖拽模式
        if (e.button === 0) {
          this.isDragging = true;
          this.lastDragPos = { x: e.clientX, y: e.clientY };
          this.$refs.container.classList.add("dragging");
        }
      } else {
        // 正常绘制模式
        if (e.button !== 0) return;

        this.isDrawing = true;
        const pos = this.getCanvasCoords(e);

        this.fgCtx.beginPath();
        this.fgCtx.moveTo(pos.x, pos.y);
      }
    },

    handleCanvasMouseMove(e) {
      const pos = this.getCanvasCoords(e);
      this.mouseX = pos.x;
      this.mouseY = pos.y;

      if (this.isDragging) {
        // 拖拽画布
        const dx = e.clientX - this.lastDragPos.x;
        const dy = e.clientY - this.lastDragPos.y;

        this.offset.x += dx;
        this.offset.y += dy;

        this.lastDragPos = { x: e.clientX, y: e.clientY };
        this.applyTransform();
      } else if (this.isDrawing) {
        // 绘制操作
        this.fgCtx.globalCompositeOperation = this.isErasing
          ? "destination-out"
          : "source-over";

        this.fgCtx.lineTo(pos.x, pos.y);
        this.fgCtx.stroke();
      } else {
        this.cursorVisible = true;
      }
    },

    handleCanvasMouseUp(e) {
      if (this.isDragging) {
        this.isDragging = false;
        this.$refs.container.classList.remove("dragging");
      } else if (this.isDrawing) {
        this.fgCtx.closePath();
        this.isDrawing = false;
        this.saveState();
      }
    },

    handleCanvasMouseLeave() {
      this.cursorVisible = false;
      if (this.isDrawing) {
        this.fgCtx.closePath();
        this.isDrawing = false;
        this.saveState();
      }
      if (this.isDragging) {
        this.isDragging = false;
        this.$refs.container.classList.remove("dragging");
      }
    },

    toggleEraseMode() {
      this.isErasing = !this.isErasing;
    },

    updateCursorPosition(e) {
      const rect = this.$refs.container.getBoundingClientRect();
      this.cursorX = e.clientX - rect.left;
      this.cursorY = e.clientY - rect.top;
    },

    // 精确坐标计算
    getCanvasCoords(e) {
      const rect = this.$refs.container.getBoundingClientRect();
      const scale = this.scale;
      const offset = this.offset;

      return {
        x: (e.clientX - rect.left - offset.x) / scale,
        y: (e.clientY - rect.top - offset.y) / scale,
      };
    },

    handleWheel(e) {
      this.ishandleWheel=true;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const oldScale = this.scale;
      this.scalePercent = Math.max(
        10,
        Math.min(200, this.scalePercent + delta * 10)
      );

      // 获取鼠标在容器中的位置
      const rect = this.$refs.container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // 计算鼠标在画布上的位置（基于旧比例）
      const canvasX = (mouseX - this.offset.x) / oldScale;
      const canvasY = (mouseY - this.offset.y) / oldScale;

      // 更新偏移量以保持鼠标下的点不变
      this.offset.x = mouseX - canvasX * this.scale;
      this.offset.y = mouseY - canvasY * this.scale;

      this.applyTransform();
    },

    applyTransform() {
      this.bgCanvas.style.transform = `translate(${this.offset.x}px, ${this.offset.y}px) scale(${this.scale})`;
      this.bgCanvas.style.transformOrigin = "0 0";
      this.fgCanvas.style.transform = `translate(${this.offset.x}px, ${this.offset.y}px) scale(${this.scale})`;
      this.fgCanvas.style.transformOrigin = "0 0";
    },

    zoomIn() {
      this.scalePercent = Math.min(200, this.scalePercent + 10);
    },

    zoomOut() {
      this.scalePercent = Math.max(10, this.scalePercent - 10);
    },

    resetZoom() {
      this.scalePercent = Number(((600/this.originalImage.height) * 100).toFixed(1));
      this.centerCanvas();
    },

    saveState() {
      this.undoStack.push(this.fgCanvas.toDataURL());
      this.redoStack = [];
    },

    undo() {
      if (this.undoStack.length > 1) {
        this.redoStack.push(this.undoStack.pop());
        this.restoreState(this.undoStack[this.undoStack.length - 1]);
      }
    },

    redo() {
      if (this.redoStack.length > 0) {
        const state = this.redoStack.pop();
        this.undoStack.push(state);
        this.restoreState(state);
      }
    },

    restoreState(dataUrl) {
      const img = new Image();
      img.onload = () => {
        this.fgCtx.globalCompositeOperation = "source-over";
        this.fgCtx.clearRect(0, 0, this.fgCanvas.width, this.fgCanvas.height);
        this.fgCtx.drawImage(img, 0, 0);
      };
      img.src = dataUrl;
    },

    handleResize() {
      this.centerCanvas();
    },

    handleKeyDown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        this.undo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "y") {
        e.preventDefault();
        this.redo();
      } else if (e.key === " ") {
        e.preventDefault();
        this.spacePressed = true;
        this.$refs.container.classList.add("panning");
      }
    },

    handleKeyUp(e) {
      if (e.key === " ") {
        this.spacePressed = false;
        this.isDragging = false;
        this.$refs.container.classList.remove("panning", "dragging");
      }
    },
    sureMask(){
      if(this.binaryPreview){
        this.$emit('confirm',this.binaryPreview)
      }else{
        this.$message.warning('暂无已生成遮罩');
      }
    },
    generateMask() {
      // 创建临时canvas处理原始图像
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = this.fgCanvas.width;
      tempCanvas.height = this.fgCanvas.height;
      const tempCtx = tempCanvas.getContext("2d");

      // 绘制原始图像
      tempCtx.drawImage(
        this.originalImage,
        0,
        0,
        tempCanvas.width,
        tempCanvas.height
      );

      // 创建蒙版
      const maskCanvas = document.createElement("canvas");
      maskCanvas.width = this.fgCanvas.width;
      maskCanvas.height = this.fgCanvas.height;
      const maskCtx = maskCanvas.getContext("2d");
      maskCtx.drawImage(this.fgCanvas, 0, 0);
      const maskData = maskCtx.getImageData(
        0,
        0,
        maskCanvas.width,
        maskCanvas.height
      );
      const maskPixels = maskData.data;

      for (let i = 0; i < maskPixels.length; i += 4) {
        maskPixels[i] = 255; // R
        maskPixels[i + 1] = 255; // G
        maskPixels[i + 2] = 255; // B
        // A通道保持不变
      }

      maskCtx.putImageData(maskData, 0, 0);

      // 使用蒙版提取原图的相应区域
      tempCtx.globalCompositeOperation = "destination-in";
      tempCtx.drawImage(maskCanvas, 0, 0);
      tempCtx.globalCompositeOperation = "source-over";

      // 其余代码保持不变...
      let svgToBase64image = tempCanvas.toDataURL("image/png");
      let fileimage = this.dataURLtoFile(svgToBase64image, "cropMask.png");
      const formData = new FormData();
      formData.append("image", fileimage);
      this.cropMaskloading = true;
      uploadImage(formData)
        .then((res) => {
          this.cropMaskloading = false;
          this.binaryPreview = res.data.url;
        })
        .catch((err) => {
          this.cropMaskloading = false;
        });
    },

    dataURLtoFile: function (dataurl, filename) {
      var arr = dataurl.split(","),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    },

    handleClose() {
      // 清理资源
      window.removeEventListener("resize", this.handleResize);
      window.removeEventListener("keydown", this.handleKeyDown);
      window.removeEventListener("keyup", this.handleKeyUp);
      this.$emit('close');
    },

    handleConfirm() {
      this.$emit("confirm", this.binaryPreview);
      this.handleClose();
    },
  },
  beforeDestroy() {
    // 确保移除事件监听
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("keyup", this.handleKeyUp);
  },
};
</script>

<style lang="scss" scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

body {
  background: linear-gradient(135deg, #1a2a6c, #b21f1f, #fdbb2d);
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.demo-container {
  max-width: 800px;
  width: 100%;
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.25);
  text-align: center;
}

.demo-title {
  font-size: 2.2rem;
  color: white;
  margin-bottom: 25px;
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

.demo-image {
  max-width: 100%;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  margin: 20px 0;
  cursor: pointer;
  transition: all 0.3s ease;
}

.demo-image:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
}

.editor-container {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
}

.tools-panel {
  flex: 1;
  min-width: 300px;
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.canvas-wrapper {
  flex: 2;
  min-width: 500px;
  background: #f8f9fa;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  position: relative;
}

.tool-group {
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.tool-group:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.tool-title {
  font-size: 1.2rem;
  color: #2c3e50;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  font-weight: 600;
}

.tool-title i {
  margin-right: 10px;
  font-size: 1.3rem;
  color: #3498db;
}

.tool-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
}

.btn {
  padding: 12px 18px;
  border: none;
  border-radius: 8px;
  background: #3498db;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08);
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 7px 14px rgba(50, 50, 93, 0.1), 0 3px 6px rgba(0, 0, 0, 0.08);
}

.btn:active {
  transform: translateY(1px);
}

.btn:disabled {
  background: #bdc3c7;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-undo {
  background: #0095FF;
}

.btn-redo {
  background: #f39c12;
}

.btn-erase {
  background: #e74c3c;
}

.btn-generate {
  background: #9b59b6;
  width: 100%;
  justify-content: center;
  font-size: 1.1rem;
  padding: 15px;
  margin-top: 10px;
}

.slider-container {
  display: flex;
  align-items: center;
  gap: 15px;
  margin: 10px 0;
}

.slider-value {
  background: #3498db;
  color: white;
  border-radius: 20px;
  width: 60px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

input[type="range"] {
  flex: 1;
  height: 8px;
  -webkit-appearance: none;
  background: #e0e0e0;
  border-radius: 10px;
  outline: none;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #3498db;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.canvas-container {
  position: relative;
  overflow: auto;
  height: 600px;
  border: 2px dashed #bdc3c7;
  border-radius: 10px;
  background: #2c3e50;
  background-image: linear-gradient(45deg, #34495e 25%, transparent 25%),
    linear-gradient(-45deg, #34495e 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #34495e 75%),
    linear-gradient(-45deg, transparent 75%, #34495e 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  cursor: default;
}

.canvas-container.panning {
  cursor: grab;
}

.canvas-container.panning.dragging {
  cursor: grabbing;
}

canvas {
  display: block;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.2);
  cursor: none;
  position: absolute;
  top: 0;
  left: 0;
}

.cursor {
  position: absolute;
  border-radius: 50%;
  border: 2px solid #fff;
  pointer-events: none;
  z-index: 10;
  transform: translate(-50%, -50%);
  box-sizing: border-box;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}

.zoom-controls {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  z-index: 5;
}

.zoom-display {
  text-align: center;
  font-weight: bold;
  color: #2c3e50;
  padding: 5px;
}

.mask-preview {
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.preview-title {
  font-size: 1.3rem;
  color: #2c3e50;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.preview-container {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.preview-item {
  flex: 1;
  min-width: 300px;
  background: #f8f9fa;
  border-radius: 10px;
  padding: 15px;
  text-align: center;
}

.preview-item h3 {
  margin-bottom: 10px;
  color: #7f8c8d;
}

.preview-image {
  width: 100%;
  height: 200px;
  background: #34495e;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  overflow: hidden;
}

.preview-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.key-hint {
  background: #f1f2f6;
  border-radius: 6px;
  padding: 8px 15px;
  font-size: 0.9rem;
  color: #060606;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 5px;
}

.key {
  background: #2c3e50;
  color: white;
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: bold;
}

.status-bar {
  position: absolute;
  bottom: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 0.9rem;
  z-index: 5;
}

.cursor-hint {
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 0.9rem;
  z-index: 5;
}

.drag-hint {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 0.9rem;
  z-index: 5;
}

.alert {
  position: fixed;
  top: 20px;
  right: 20px;
  background: #27ae60;
  color: white;
  padding: 15px 25px;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: slideIn 0.3s ease, fadeOut 0.5s ease 2s forwards;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@media (max-width: 900px) {
  .editor-container {
    flex-direction: column;
  }

  .canvas-wrapper {
    min-width: 100%;
  }
}

.dialog-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-top: 1px solid #eee;
}

.download-btn {
  background: linear-gradient(45deg, #3498db, #9b59b6);
  color: white;
  border: none;
  padding: 10px 25px;
  border-radius: 6px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
}

.download-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}
</style>